import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CHARLIE } from "../lib/api";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function CharlieHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const charlieY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const shadowY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const dustY = useTransform(scrollYProgress, [0, 1], [0, -220]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
      <motion.div style={{ y: dustY }} className="hero-blob w-[380px] h-[380px] -left-24 top-24 bg-[#D4A373]/40" />
      <motion.div style={{ y: dustY }} className="hero-blob w-[520px] h-[520px] right-[-160px] top-40 bg-[#4A7C7B]/25" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-center">
        <div className="lg:col-span-7 relative z-10">
          <div className="mono text-[11px] tracking-[0.28em] uppercase text-[#4A7C7B] mb-8 reveal" style={{ animationDelay: "0.1s" }}>
            The Love Well Project · Book One — Coming Soon
          </div>

          <h1 className="serif text-[clamp(3rem,8vw,7rem)] leading-[0.98] tracking-tight text-espresso">
            <span className="reveal block" style={{ animationDelay: "0.2s" }}>Some people</span>
            <span className="reveal block italic text-[#4A7C7B]" style={{ animationDelay: "0.35s" }}>love well.</span>
            <span className="reveal block" style={{ animationDelay: "0.5s" }}>Others love</span>
            <span className="reveal block italic" style={{ animationDelay: "0.65s" }}>differently.</span>
          </h1>
          <div
            className="reveal serif text-[clamp(1.35rem,3vw,2.25rem)] leading-[1.15] tracking-tight text-espresso/70 italic mt-4"
            style={{ animationDelay: "0.85s" }}
          >
            Your child is loved either way.
          </div>

          <p className="mt-8 md:mt-10 max-w-xl text-base md:text-lg leading-relaxed text-charcoal reveal" style={{ animationDelay: "0.9s" }}>
            A picture-book series written to sit with children through the tender, difficult places — parental absence, complicated grown-ups, and the quiet inner voice that helps them find their footing. Made for the read-aloud shelf at home, the counselor's office, and the classroom circle.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4 reveal" style={{ animationDelay: "1.1s" }}>
            <Link
              to="/waitlist"
              data-testid="hero-waitlist-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-[#2C241B] text-[#FAF8F5] pl-6 pr-4 py-3.5 hover:bg-[#4A7C7B] transition-colors"
            >
              Join the waitlist
              <span className="w-8 h-8 grid place-items-center rounded-full bg-[#FAF8F5]/15 group-hover:bg-[#FAF8F5]/25 transition">
                <ArrowRight size={16} />
              </span>
            </Link>
            <Link to="/charlie" data-testid="hero-meet-charlie" className="link-draw text-sm tracking-wide">
              Meet Charlie →
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div style={{ y: charlieY }} className="relative">
            <div className="absolute -inset-8 spotlight rounded-[42%_58%_46%_54%/56%_44%_58%_42%]" aria-hidden />
            <div className="charlie-float relative">
              <img
                src={CHARLIE.teal}
                alt="Charlie the owl in blue overalls and green glasses"
                className="relative w-full max-w-[520px] mx-auto rounded-[38%_62%_44%_56%/54%_46%_54%_46%] shadow-[0_60px_120px_-40px_rgba(44,36,27,0.4)]"
                loading="eager"
              />
            </div>
            <motion.div
              style={{ y: shadowY }}
              className="mx-auto mt-2 h-6 w-2/3 rounded-full bg-[#2C241B]/15 blur-2xl"
              aria-hidden
            />
          </motion.div>

          <div className="mt-6 max-w-sm mx-auto text-center reveal" style={{ animationDelay: "1.3s" }}>
            <div className="mono text-[10px] tracking-[0.28em] uppercase text-[#4A7C7B]">Fig. 01</div>
            <div className="italic-serif text-lg text-espresso/90 mt-1">
              Charlie — a small owl with a small strong voice.
            </div>
            <div className="italic-serif text-xs text-charcoal/55 mt-3">
              Illustrations by Zuzana Svobodová
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
